// Import a CSV / TSV / XLSX file into a table of the hosted database, with a validation preview
// (routers/data_imports.py; operate/imports.py). Three steps on one card:
//   1. choose the file and the mode (append / append and update by primary key) -> upload: the
//      server stores it and checks every row with the same validator as the form (POST .../imports);
//   2. the column mapping (by name, adjustable; "Check again" = PUT .../imports/{id}) and the
//      preview: counts, the problems (row, column, value, message) in an accessible table, the
//      primary keys already in the table, and in update mode the conflicts (a file value against
//      a non-empty database value; the database's value is kept unless ticked);
//   3. the commit (all or nothing, or valid rows only) -> POST .../imports/{id}/commit.
// The XLSX report and the rejected rows download at any time; "Discard" deletes the pending file.
// A filled spreadsheet template (TemplateDownloads) is recognised by the server (preview.template):
// its table's sheet is read and mapped by name, so the panel says so and keeps the mapping closed.
// Files beside the table (task "files are uploaded into a live database"): while the import is
// pending, files, a .zip of files or a checksum manifest (md5sums.txt, *.md5) are delivered with it
// (POST .../imports/{id}/files, one request per chosen file; routers/data_files.py); the preview then
// pairs them with the rows by the file-name column and lists rows without a file, files without
// a row, wrong extensions and checksum mismatches (liveFiles.filesSummary).
// When the project reviews imports (`review_required`), step 3 SUBMITS the import instead: a data
// steward approves or rejects it on the Data page (ImportReviewPanel); nothing is written until then.
// `resumeId` opens an existing pending import of the caller instead of the upload form: one a
// steward's approval RETURNED (the database changed since it was submitted), with the reason, to
// check again and submit again (DataTablePage `?import=<id>`, linked from ImportReviewPanel).
import { useEffect, useState } from "react";
import { api } from "../../api";
import { ErrorNote } from "../../components/Feedback";
import { Badge, Btn, Card, Field, Select } from "../../components/Shared";
import { md5OfFile, uploadErrorText } from "../../components/UploadPanel";
import { canCommit, issueRows, mappingForApi, MODES, ON_ERROR, resultText, summaryText } from "./importUtils";
import { filesSummary, fileSize, importFilesPath } from "./liveFiles";

const srOnly = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)",
                 whiteSpace: "nowrap" };
const th = { textAlign: "left", padding: "6px 10px", borderBottom: "1px solid #e5e7eb", fontSize: 12,
             background: "#f9fafb" };
const td = { padding: "6px 10px", borderTop: "1px solid #f3f4f6", fontSize: 13, verticalAlign: "top" };

// Each option row is padded so two radios are at least 24 px apart (axe target-size, 2026-10-21);
// padding, not min-height, so the phone layer's 44 px min-height for a label with a radio still wins.
function Choice({ name, legend, options, value, onChange }) {
  return (
    <fieldset style={{ border: "none", padding: 0, margin: "0 0 12px" }}>
      <legend style={{ fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.05em",
                       textTransform: "uppercase", marginBottom: 6 }}>{legend}</legend>
      {options.map(o => (
        <label key={o.value} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, margin: "4px 0", padding: "3px 0" }}>
          <input type="radio" name={name} value={o.value} checked={value === o.value}
                 onChange={() => onChange(o.value)} />
          {o.label}
        </label>
      ))}
    </fieldset>
  );
}

function IssuesTable({ issues }) {
  if (!issues.length) return null;
  return (
    <div data-table-scroll role="region" aria-label="Problems found" tabIndex={0} style={{ overflow: "auto", maxHeight: 320, marginBottom: 12 }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <caption style={srOnly}>Problems found in the file</caption>
        <thead>
          <tr>
            <th scope="col" style={th}>Row</th>
            <th scope="col" style={th}>Column</th>
            <th scope="col" style={th}>Value</th>
            <th scope="col" style={th}>Severity</th>
            <th scope="col" style={th}>Problem</th>
          </tr>
        </thead>
        <tbody>
          {issues.map(i => (
            <tr key={i.key}>
              <td style={td}>{i.row ?? "—"}</td>
              <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{i.column ?? "—"}</td>
              <td style={td}>{i.value ?? ""}</td>
              <td style={td}><Badge color={i.severity === "error" ? "red" : i.severity === "warning" ? "amber" : "blue"}>{i.severity}</Badge></td>
              <td style={td}>{i.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConflictsTable({ conflicts, accepted, onToggle }) {
  if (!conflicts.length) return null;
  return (
    <div data-table-scroll role="region" aria-label="Conflicts" tabIndex={0} style={{ overflow: "auto", maxHeight: 320, marginBottom: 12 }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <caption style={{ textAlign: "left", fontSize: 13, padding: "4px 0 8px", color: "#374151" }}>
          Cells where the file differs from a value already in the database. The database's value is
          kept unless you tick the file's.
        </caption>
        <thead>
          <tr>
            <th scope="col" style={th}>Use the file's value</th>
            <th scope="col" style={th}>Row</th>
            <th scope="col" style={th}>Key</th>
            <th scope="col" style={th}>Column</th>
            <th scope="col" style={th}>In the file</th>
            <th scope="col" style={th}>In the database</th>
          </tr>
        </thead>
        <tbody>
          {conflicts.map(c => (
            <tr key={c.id}>
              <td style={td}>
                <input type="checkbox" checked={accepted.has(c.id)} onChange={() => onToggle(c.id)}
                       aria-label={`Use the file's value for ${c.column} of ${c.key}`} />
              </td>
              <td style={td}>{c.row}</td>
              <td style={td}>{c.key}</td>
              <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{c.column}</td>
              <td style={td}>{c.file_value}</td>
              <td style={td}>{c.db_value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeliveredFiles({ imp, busy, onAdd }) {
  const lines = filesSummary(imp.preview && imp.preview.files);
  const files = imp.attachments || [];
  return (
    <section aria-label="Files delivered with the rows" style={{ margin: "0 0 14px", padding: "10px 12px",
                                                                 border: "1px solid #e5e7eb", borderRadius: 8 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Files delivered with the rows</h3>
      <p style={{ margin: "0 0 8px", fontSize: 13, color: "#4b5563" }}>
        The files the file-name column names (for example FASTQs): choose them, or one .zip of them. A checksum
        manifest (md5sums.txt or *.md5) from the sequencing facility is compared with every file.
      </p>
      <label htmlFor="import-delivered-files" style={{ fontSize: 13, fontWeight: 600, marginRight: 8 }}>
        Add files, a .zip or a manifest
      </label>
      <input id="import-delivered-files" type="file" multiple disabled={!!busy}
             onChange={e => { const chosen = Array.from(e.target.files || []); e.target.value = ""; onAdd(chosen); }} />
      {busy === "files" && <p role="status" style={{ fontSize: 13 }}>Delivering the files…</p>}
      {lines.length > 0 && (
        <ul aria-label="Pairing of rows and files" style={{ fontSize: 13, margin: "8px 0 0", paddingLeft: 18 }}>
          {lines.map((l, n) => <li key={n}>{l}</li>)}
        </ul>
      )}
      {files.length > 0 && (
        <ul aria-label="Delivered files" style={{ fontSize: 12, margin: "6px 0 0", paddingLeft: 18, color: "#374151" }}>
          {files.map(f => (
            <li key={f.object_id}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{f.filename}</span> ({fileSize(f.size)})
              {(f.problems || []).length > 0 && <span style={{ color: "#b91c1c" }}> — {f.problems.join("; ")}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function ImportPanel({ projectId, table, columns, onDone, onCancel, resumeId }) {
  const base = `/projects/${projectId}/data/tables/${encodeURIComponent(table)}/imports`;
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("append");
  const [imp, setImp] = useState(null);
  const [mapping, setMapping] = useState({});
  const [onError, setOnError] = useState("all_or_nothing");
  const [accepted, setAccepted] = useState(new Set());
  const [busy, setBusy] = useState("");
  const [error, setError] = useState(null);

  const take = data => {
    setImp(data);
    setMapping(data.mapping || {});
    setMode(data.mode);
    setAccepted(new Set());
  };
  const run = async (what, fn) => {
    setBusy(what);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err.status ? new Error(uploadErrorText(err)) : err);
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    if (!resumeId) return;
    run("load", async () => {
      const data = await api.get(`${base}/${resumeId}`);
      take(data);
      if (data.on_error) setOnError(data.on_error);
    });
  }, [resumeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = () => run("upload", async () => {
    if (!file) return;
    if (file.size === 0) throw new Error("The file is empty (0 bytes).");
    const md5 = await md5OfFile(file);
    take(await api.upload(`${base}?mode=${mode}`, file, { md5 }));
  });
  const recheck = () => run("check", async () => {
    take(await api.put(`${base}/${imp.id}`, { mapping: mappingForApi(mapping), mode }));
  });
  const commit = () => run("commit", async () => {
    try {
      const done = await api.post(`${base}/${imp.id}/commit`,
                                  { on_error: onError, accept_conflicts: Array.from(accepted) });
      setImp(done);
      onDone(done);
    } catch (err) {
      if (err.status === 422 || err.status === 409) {   // the database changed since the preview: show the new one
        try { take(await api.get(`${base}/${imp.id}`)); } catch { /* keep the old preview */ }
      }
      throw err;
    }
  });
  const deliver = chosen => run("files", async () => {
    for (const f of chosen) {
      if (f.size === 0) throw new Error(`${f.name} is empty (0 bytes).`);
      const md5 = await md5OfFile(f);
      take(await api.upload(importFilesPath(projectId, table, imp.id), f, { md5 }));
    }
  });
  const discard = () => run("discard", async () => {
    await api.del(`${base}/${imp.id}`);
    onCancel();
  });
  const download = (path, name) => run("download", () => api.download(`${base}/${imp.id}/${path}`, name));

  const p = imp && imp.preview;
  const check = canCommit(p, onError);
  const stem = imp ? imp.filename.replace(/\.[^.]+$/, "") : "";
  const ext = imp ? (imp.filename.match(/\.[^.]+$/) || [""])[0] : "";
  const targets = columns.map(c => c.name);
  const mappingChanged = imp && (mode !== imp.mode
    || JSON.stringify(mappingForApi(mapping)) !== JSON.stringify(mappingForApi(imp.mapping)));

  return (
    <Card style={{ marginBottom: 16 }}>
      <section aria-label={`Import a file into ${table}`}>
        <h2 style={{ margin: "0 0 12px", fontSize: 18 }}>Import a file into {table}</h2>
        <ErrorNote error={error} />
        {!imp && busy !== "load" && (
          <>
            <Field label="File (CSV, TSV or XLSX — the first sheet, or a filled template's sheet of this table)">
              <input type="file" accept=".csv,.tsv,.txt,.xlsx" onChange={e => setFile(e.target.files[0] || null)} />
            </Field>
            <div style={{ height: 12 }} />
            <Choice name="import-mode" legend="What to do with the rows" options={MODES} value={mode} onChange={setMode} />
            <div style={{ display: "flex", gap: 10 }}>
              <Btn onClick={upload} disabled={!file || !!busy}>{busy === "upload" ? "Checking the file…" : "Upload and check"}</Btn>
              <Btn variant="secondary" onClick={onCancel} disabled={!!busy}>Cancel</Btn>
            </div>
          </>
        )}
        {imp && imp.status === "committed" && (
          <p role="status" style={{ fontSize: 14, color: "#006b2d" }}>{resultText(imp.result)}</p>
        )}
        {imp && imp.status === "pending" && imp.review_decision === "returned" && (
          <p role="status" style={{ fontSize: 14, color: "#92400e" }}>
            Returned by {imp.reviewed_by || "the reviewer"}: {imp.review_comment} Check the new preview
            below and submit it again.
          </p>
        )}
        {imp && imp.status === "submitted" && (
          <p role="status" style={{ fontSize: 14, color: "#1d4ed8" }}>
            Submitted for review: a data steward approves or rejects it; nothing is written until then.
            It is listed under "Imports waiting for review" on the Data page.
          </p>
        )}
        {imp && imp.status === "pending" && p && (
          <>
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "#4b5563" }}>
              <strong>{imp.filename}</strong> — the file is kept until it is imported or discarded
              (at most until {new Date(imp.expires_at).toLocaleString("en")}).
            </p>
            {p.template && (
              <p role="note" style={{ margin: "0 0 10px", fontSize: 13, color: "#006b2d" }}>
                Recognised as the unio template of {p.template.table} (design version {p.template.version}, sheet{" "}
                <strong>{p.template.sheet}</strong>): its columns are mapped by name.
                {!p.template.current && ` The hosted database is at version ${p.template.hosted_version}: check the problems below, or download the current template.`}
              </p>
            )}
            <details open={!p.template && Object.values(imp.mapping || {}).some(v => !v)} style={{ marginBottom: 12 }}>
              <summary style={{ cursor: "pointer", fontSize: 14, fontWeight: 600 }}>Columns of the file → columns of {table}</summary>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, marginTop: 10 }}>
                {(imp.file_columns || []).map(fc => (
                  <Field key={fc} label={fc} hint="— select — : not imported">
                    <Select options={targets} value={mapping[fc] || ""}
                            onChange={e => setMapping(m => ({ ...m, [fc]: e.target.value || null }))} />
                  </Field>
                ))}
              </div>
              <div style={{ height: 10 }} />
              <Choice name="import-mode-again" legend="What to do with the rows" options={MODES} value={mode} onChange={setMode} />
              <Btn variant="secondary" onClick={recheck} disabled={!!busy || !mappingChanged}>
                {busy === "check" ? "Checking…" : "Check again"}
              </Btn>
            </details>
            <p role="status" style={{ fontSize: 14, margin: "0 0 10px" }}>{summaryText(p)}</p>
            <DeliveredFiles imp={imp} busy={busy} onAdd={deliver} />
            {p.blocking.length > 0 && (
              <ul aria-label="Why the file cannot be imported" style={{ color: "#b91c1c", fontSize: 13 }}>
                {p.blocking.map((b, n) => <li key={n}>{b.column ? `${b.column}: ` : ""}{b.message}</li>)}
              </ul>
            )}
            {p.duplicates.length > 0 && (
              <p style={{ fontSize: 13, color: "#92400e" }}>
                {p.duplicates.length} row(s) name a primary key that is in the table already
                ({p.duplicates.slice(0, 5).map(d => d.key).join(", ")}{p.duplicates.length > 5 ? ", …" : ""}).
                Choose "Append, and update rows …" to change those rows instead.
              </p>
            )}
            <IssuesTable issues={issueRows(p)} />
            {p.truncated && <p style={{ fontSize: 12, color: "#6b7280" }}>Only the first problems of each kind are listed; the XLSX report has all of them.</p>}
            <ConflictsTable conflicts={p.conflicts || []} accepted={accepted}
                            onToggle={id => setAccepted(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; })} />
            <Choice name="import-on-error" legend="When rows have errors" options={ON_ERROR} value={onError} onChange={setOnError} />
            {!check.ok && check.why && <p style={{ fontSize: 13, color: "#6b7280" }}>{check.why}</p>}
          </>
        )}
        {imp && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {imp.status === "pending" && (
              <Btn onClick={commit} disabled={!check.ok || !!busy || mappingChanged}>
                {busy === "commit" ? (imp.review_required ? "Submitting…" : "Importing…")
                  : (imp.review_required ? "Submit for review" : "Import")}
              </Btn>
            )}
            <Btn variant="secondary" onClick={() => download("report.xlsx", `${stem}.report.xlsx`)} disabled={!!busy}>
              Download XLSX report
            </Btn>
            {p && p.error_rows > 0 && (
              <Btn variant="secondary" onClick={() => download("rejected", `${stem}.rejected${ext}`)} disabled={!!busy}>
                Download rows with errors
              </Btn>
            )}
            {imp.status === "pending"
              ? <Btn variant="danger" onClick={discard} disabled={!!busy}>Discard</Btn>
              : <Btn variant="secondary" onClick={onCancel}>Close</Btn>}
          </div>
        )}
      </section>
    </Card>
  );
}
